import clsx from 'clsx'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/24/solid'
import { ReactNode, useEffect, useState } from 'react'
import { BounceLoader } from 'react-spinners'
import { Avatar } from './avatar'
import { Inline as Loading } from '@/components/loading'
import { useInfiniteScroll } from '@/helpers/useInfiniteScroll'

export interface TSearchable {
  id: string | null
  name: string
  detail?: ReactNode | string | null
  image?: string
}

type TVariables = Record<string, Record<string, string | undefined>>

interface Props<T extends TSearchable> {
  data: T[]
  filters?: Record<string, string | null>
  loading: boolean
  refetch: (options: TVariables) => void
  moreToFetch: boolean | undefined
  fetchMore: () => void
  searchField: string
  filterFields?: string[]
  value: T
  onChange: (value: T | null) => void
  invalid: boolean
  includeBlank?: boolean
  placeholder?: string
  allowCustomInput?: boolean
}

export const SearchableCombobox = <T extends TSearchable>({ data, filters = {}, loading, refetch, moreToFetch = false, fetchMore, searchField, filterFields = ['filters'], value, onChange, invalid, includeBlank = true, placeholder, allowCustomInput = false }: Props<T>) => {
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | undefined>()
  const [searchQuery, setSearchQuery] = useState<string>('')

  const { setLoaderElement } = useInfiniteScroll({ fetchMore })

  useEffect(() => {
    clearTimeout(searchTimeout)
    setSearchTimeout(
      setTimeout(() => {
        const searchFilters = { ...filters, [searchField]: searchQuery }
        refetch(filterFields.reduce((variables, filterField) => ({ ...variables, [filterField]: searchFilters }), {}))
      }, 150)
    )
  }, [searchQuery])

  const dataset = includeBlank ? [{ id: null, name: 'None' } as TSearchable, ...data] : data
  const usingImages = dataset.map(v => v.image).filter(v => v != undefined).length > 0

  return (
    <>
      <Combobox
        as="span"
        by="id"
        data-slot="control"
        value={value}
        className={clsx([
          // Basic layout
          'relative block w-full',
          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm',
          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          'dark:before:hidden',
          // Focus ring
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500',
          // Disabled state
          'has-data-disabled:opacity-50 has-data-disabled:before:bg-zinc-950/5 has-data-disabled:before:shadow-none',
          // Invalid state
          'has-data-invalid:before:shadow-red-500/10',
        ])}
        immediate
        onChange={(option: T | null) => {
          // fireSearch('')
          onChange(option)
        }}
      >
        <ComboboxInput
          displayValue={(value: T) => value?.name || ''}
          placeholder={placeholder}
          className={clsx([
            // Basic layout
            'relative block w-full appearance-none rounded-lg px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
            // Typography
            'text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white',
            // Border
            'border',
            // Background color
            'bg-transparent dark:bg-white/5',
            // Hide default focus styles
            'focus:outline-hidden',
            // Invalid state
            invalid ? 'border-red-500 data-hover:border-red-500 dark:border-red-500 dark:data-hover:border-red-500' : 'border-zinc-950/10 data-hover:border-zinc-950/20 dark:border-white/10 dark:data-hover:border-white/20',
            // Disabled state
            'data-disabled:border-zinc-950/20 dark:data-hover:data-disabled:border-white/15 dark:data-disabled:border-white/15 dark:data-disabled:bg-white/[2.5%]',
            // System icons
            'dark:[color-scheme:dark]',
          ])}
          onChange={event => setSearchQuery(event.target.value)}
        />
        { loading && <span className="absolute inset-y-0 right-8 flex items-center"><BounceLoader color="lightgray" size={12} /></span> }
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg
              aria-hidden="true"
              className="size-5 stroke-zinc-500 group-has-data-disabled:stroke-zinc-600 sm:size-4 dark:stroke-zinc-400 forced-colors:stroke-[CanvasText]"
              fill="none"
              viewBox="0 0 16 16"
            >
              <path
                d="M5.75 10.75L8 13L10.25 10.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
              />
              <path
                d="M10.25 5.25L8 3L5.75 5.25"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
              />
            </svg>
          </span>
        </ComboboxButton>
        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-zinc-950/5 *:bg-zinc-50 dark:bg-white/5 dark:*:bg-zinc-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden sm:text-sm transition duration-200 ease-out empty:invisible data-closed:scale-95 data-closed:opacity-0" transition>
          { allowCustomInput && searchQuery != '' && (
            <ComboboxOption
              className="flex gap-2 items-center group relative cursor-default select-none py-2 pl-8 pr-4 text-zinc-950 dark:text-white data-focus:bg-sky-700 data-focus:text-white"
              value={{ id: searchQuery, name: searchQuery }}
            >
              { usingImages && <span className="w-12" /> }
              <span className="block truncate group-data-selected:font-semibold">Create {searchQuery}</span>
              <span className="absolute inset-y-0 left-0 hidden items-center pl-1.5 text-sky-600 group-data-selected:flex group-data-focus:text-white">
                <CheckIcon aria-hidden="true" className="h-5 w-5" />
              </span>
            </ComboboxOption>
          )}
          { dataset.length > 0 && (
            <>
              { dataset.map(option => (
                <ComboboxOption
                  key={option.id}
                  className="flex gap-2 items-center group relative cursor-default select-none py-2 pl-8 pr-4 text-zinc-950 dark:text-white data-focus:bg-sky-700 data-focus:text-white"
                  value={option}
                >
                  { usingImages && <span className="w-12">
                    { option.image && <Avatar className="bg-white size-12" src={option.image} square /> }
                  </span> }
                  <span className="block truncate group-data-selected:font-semibold">{option.name}</span>
                  { option.detail && <span className="text-zinc-600 dark:text-zinc-400 group-data-focus:text-white">{ option.detail }</span> }
                  <span className="absolute inset-y-0 left-0 hidden items-center pl-1.5 text-sky-600 group-data-selected:flex group-data-focus:text-white">
                    <CheckIcon aria-hidden="true" className="h-5 w-5" />
                  </span>
                </ComboboxOption>
              ))}
              {
                moreToFetch &&
                <ComboboxOption
                  key="loading"
                  ref={setLoaderElement}
                  className="flex gap-2 items-center group relative cursor-default select-none py-2 pl-8 pr-4 text-zinc-950 dark:text-white data-focus:bg-sky-700 data-focus:text-white"
                  value={null}
                >
                  <span className="block truncate group-data-selected:font-semibold"><Loading /></span>
                </ComboboxOption>
              }
            </>
          )}
        </ComboboxOptions>
      </Combobox>
    </>
  )
}
